/**
 * Step 5 — Upload logic.
 *
 * The user drops a `.wasm` file (or any binary in demo mode). We hash
 * it with SHA-256 entirely in the browser — no upload, no server.
 * The hash is persisted into the draft; the raw bytes are held in
 * memory so the Sign step can pipe them to `createGi`.
 *
 * Demo-mode: the user can skip the upload and the Sign step will use
 * a random placeholder hash. This is clearly communicated because a
 * real backend would reject it; it exists so the wizard is navigable
 * without forcing the user to find a WASM file first.
 */

import { sha256 } from '@noble/hashes/sha256';
import { bytesToHex } from '@noble/hashes/utils';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/Button';
import { Spinner } from '@/components/LoadingState';
import type { Hash256 } from '@/api/types';
import { cn } from '@/utils/cn';
import { truncateHash } from '@/utils/format';

import { useSparkStore } from '../store';
import { WizardFooter } from './Step1Basic';

const MAX_WASM_BYTES = 16 * 1024 * 1024; // 16 MiB — matches the engine's default cap.

export function Step5Logic({
  onAdvance,
  onBack,
}: {
  onAdvance: () => void;
  onBack: () => void;
}) {
  const { t } = useTranslation('spark');
  const state = useSparkStore();
  const fileInput = useRef<HTMLInputElement | null>(null);

  const [hashing, setHashing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [dragActive, setDragActive] = useState(false);

  async function ingestFile(file: File) {
    setError(null);
    if (file.size > MAX_WASM_BYTES) {
      setError(t('steps.5.too_large', { max: '16 MiB' }));
      return;
    }
    setHashing(true);
    try {
      const bytes = new Uint8Array(await file.arrayBuffer());
      const hash: Hash256 = `sha256:${bytesToHex(sha256(bytes))}`;
      useSparkStore.getState().applyWasm(hash, bytes, file.name, bytes.byteLength);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setHashing(false);
    }
  }

  const hasFile = Boolean(state.wasm_hash);
  const hasBytesInMemory = Boolean(state.wasm_bytes);

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        onAdvance();
      }}
      className="flex flex-col gap-5"
    >
      <p className="text-sm text-ink-muted">{t('steps.5.intro')}</p>

      <div
        onDragOver={(e) => {
          e.preventDefault();
          setDragActive(true);
        }}
        onDragLeave={() => setDragActive(false)}
        onDrop={async (e) => {
          e.preventDefault();
          setDragActive(false);
          const f = e.dataTransfer.files?.[0];
          if (f) await ingestFile(f);
        }}
        className={cn(
          'flex min-h-[180px] flex-col items-center justify-center gap-3 rounded-xl border-2 border-dashed p-6 text-center transition-colors',
          dragActive
            ? 'border-spark bg-spark-soft/40'
            : hasFile
              ? 'border-spark/60 bg-spark-soft/20'
              : 'border-border bg-surface',
        )}
      >
        <input
          ref={fileInput}
          type="file"
          accept=".wasm,application/wasm,application/octet-stream"
          className="sr-only"
          onChange={async (e) => {
            const f = e.target.files?.[0];
            if (f) await ingestFile(f);
          }}
        />

        {hashing ? (
          <div className="flex items-center gap-2 text-sm text-ink-muted">
            <Spinner />
            <span>{t('steps.5.hashing')}</span>
          </div>
        ) : hasFile ? (
          <div className="flex flex-col items-center gap-2">
            <span className="flex h-10 w-10 items-center justify-center rounded-full bg-spark-soft text-spark">
              <CheckGlyph />
            </span>
            <p className="text-sm font-medium text-ink">
              {state.wasm_filename ?? t('steps.5.placeholder_filename')}
            </p>
            <p className="font-mono text-xs text-ink-muted">
              {truncateHash(state.wasm_hash!)}
            </p>
            {!hasBytesInMemory ? (
              <p className="text-xs text-warn">{t('steps.5.bytes_gone')}</p>
            ) : null}
            <Button
              type="button"
              size="sm"
              variant="ghost"
              onClick={() => {
                useSparkStore.getState().applyWasm(null, null, null, null);
                if (fileInput.current) fileInput.current.value = '';
              }}
            >
              {t('steps.5.replace')}
            </Button>
          </div>
        ) : (
          <>
            <span className="flex h-10 w-10 items-center justify-center rounded-full bg-surface-raised text-ink-muted">
              <UploadGlyph />
            </span>
            <p className="text-sm font-medium text-ink">{t('steps.5.drop_hint')}</p>
            <p className="text-xs text-ink-muted">{t('steps.5.drop_sub')}</p>
            <Button
              type="button"
              size="sm"
              variant="secondary"
              onClick={() => fileInput.current?.click()}
            >
              {t('steps.5.browse')}
            </Button>
          </>
        )}
      </div>

      {error ? (
        <p role="alert" className="text-sm text-danger">
          {error}
        </p>
      ) : null}

      <details className="rounded-md border border-border bg-surface p-4 text-sm">
        <summary className="cursor-pointer select-none text-ink-muted">
          {t('steps.5.demo_mode_title')}
        </summary>
        <p className="mt-2 text-xs text-ink-muted">{t('steps.5.demo_mode_body')}</p>
      </details>

      <WizardFooter onBack={onBack}>
        <Button type="submit" variant="primary">
          {t('actions.next', { ns: 'common' })}
        </Button>
      </WizardFooter>
    </form>
  );
}

function UploadGlyph() {
  return (
    <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M12 16V4M6 10l6-6 6 6M4 20h16" />
    </svg>
  );
}

function CheckGlyph() {
  return (
    <svg viewBox="0 0 16 16" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth={2} aria-hidden="true">
      <path d="M3 8.5 6.5 12 13 5" />
    </svg>
  );
}
